import { useMemo, useState } from "react";
import {
  Edit2,
  Trash2,
  MoreVertical,
  CheckCircle,
  AlertCircle,
  Eye,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@api/client";
import toast from "react-hot-toast";
import { Dialog } from "@components/common/Dialog";
import Button from "@components/common/Button";
import { useApprovalChains } from "@api/hooks/useApprovalChains";
import { useAllOrgUnits } from "@api/hooks/useOrgUnits";
import { useListRoles } from "@api/hooks/useRoles";
import { useListUsers } from "@api/hooks/useUsers";

interface ApprovalChain {
  id: string;
  name: string;
  description?: string;
  action_type: string;
  entity_type: string;
  document_type?: string;
  is_enabled: boolean;
  priority: number;
  step_count?: number;
  steps?: { id: string }[];
}

interface ApprovalChainsListProps {
  onCreateNew: () => void;
  onEdit: (chainId: string) => void;
  simpleMode: boolean;
}

export const ApprovalChainsList = ({
  onCreateNew,
  onEdit,
  simpleMode,
}: ApprovalChainsListProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [chainToDelete, setChainToDelete] = useState<ApprovalChain | null>(
    null,
  );
  const queryClient = useQueryClient();
  const { deleteChain, loading: isDeleting } = useApprovalChains();

  const {
    data: chainsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["approval-chains"],
    queryFn: async () => {
      const response = await apiClient.get("/approval-chains");
      return response.data;
    },
  });

  const chains = Array.isArray(chainsData)
    ? chainsData
    : chainsData?.chains || [];

  const handleDelete = async () => {
    if (!chainToDelete) return;

    try {
      await deleteChain(chainToDelete.id);
      await queryClient.invalidateQueries({ queryKey: ["approval-chains"] });
      await queryClient.removeQueries({
        queryKey: ["approval-chain", chainToDelete.id],
      });
      setExpandedId((current) =>
        current === chainToDelete.id ? null : current,
      );
      toast.success("Approval chain deleted");
      setChainToDelete(null);
    } catch (error: any) {
      toast.error(error.message || "Failed to delete approval chain");
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border border-primary-300 border-t-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200">
        Failed to load approval chains
      </div>
    );
  }

  if (chains.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-2">
          No approval chains configured
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Create your first approval chain to get started
        </p>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Create First Chain
        </button>
      </div>
    );
  }

  const documentTypeColors: Record<string, string> = {
    invoice: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    po: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
    receipt:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    ap_posting:
      "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300",
    extraction:
      "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
  };
  const chainType = (chain: ApprovalChain) => {
    if (chain.entity_type === "ap_posting")
      return { key: "ap_posting", label: "AP Posting" };
    if (chain.document_type) {
      return {
        key: chain.document_type,
        label:
          chain.document_type === "purchase_order"
            ? "Purchase Order"
            : chain.document_type,
      };
    }
    return {
      key: chain.entity_type || "extraction",
      label: chain.entity_type || "Extraction",
    };
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4">
        {chains.map((chain: ApprovalChain) => {
          const type = chainType(chain);
          const stepCount = chain.step_count ?? chain.steps?.length ?? 0;
          return (
            <div
              key={chain.id}
              className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Chain Header */}
              <div
                className="p-4 flex items-start justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                onClick={() =>
                  setExpandedId(expandedId === chain.id ? null : chain.id)
                }
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 truncate">
                        {chain.name}
                      </h3>
                      {chain.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {chain.description}
                        </p>
                      )}
                    </div>
                    {chain.is_enabled && (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    )}
                  </div>
                </div>

                {/* Status Badges */}
                <div className="flex items-center gap-2 ml-4">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium capitalize ${documentTypeColors[type.key] || "bg-gray-100 text-gray-800"}`}
                  >
                    {type.label.replace(/_/g, " ")}
                  </span>
                  <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                    {stepCount} {stepCount === 1 ? "step" : "steps"}
                  </span>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === chain.id && (
                <div className="border-t border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-800/50 space-y-4">
                  {/* Preview Section */}
                  <ChainPreview chainId={chain.id} />

                  {/* Action Buttons */}
                  <div className="flex gap-2 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => onEdit(chain.id)}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        setChainToDelete(chain);
                      }}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog
        open={!!chainToDelete}
        onOpenChange={(open) => {
          if (!open && !isDeleting) {
            setChainToDelete(null);
          }
        }}
      >
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              Delete Approval Chain?
            </h2>
          </div>

          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete{" "}
            <strong>{chainToDelete?.name}</strong>? This action cannot be
            undone.
          </p>

          <div className="flex gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setChainToDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={isDeleting}
              loading={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

// Chain preview component showing the approval flow
const ChainPreview = ({ chainId }: { chainId: string }) => {
  const { data: chainDetails } = useQuery({
    queryKey: ["approval-chain", chainId],
    queryFn: async () => {
      const response = await apiClient.get(`/approval-chains/${chainId}`);
      return response.data;
    },
  });
  const { data: rolesData } = useListRoles(0, 1000);
  const { data: usersData } = useListUsers(0, 1000);
  const { data: orgUnitsData } = useAllOrgUnits();

  const roleNameById = useMemo(
    () =>
      new Map(
        (rolesData?.items || []).map((role: { id: string; name: string }) => [
          role.id,
          role.name,
        ]),
      ),
    [rolesData],
  );
  const userNameById = useMemo(
    () =>
      new Map(
        (usersData?.items || []).map(
          (user: {
            id: string;
            first_name?: string;
            last_name?: string;
            email?: string;
          }) => [
            user.id,
            `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
              user.email ||
              user.id,
          ],
        ),
      ),
    [usersData],
  );
  const orgUnitNameById = useMemo(
    () =>
      new Map(
        (orgUnitsData || []).map((orgUnit: { id: string; name: string }) => [
          orgUnit.id,
          orgUnit.name,
        ]),
      ),
    [orgUnitsData],
  );

  if (!chainDetails) {
    return <div className="text-sm text-gray-600">Loading...</div>;
  }

  const getRoleNames = (roleIds?: string[]) => {
    if (!roleIds?.length) {
      return "N/A";
    }

    return roleIds
      .map((roleId) => roleNameById.get(roleId) || roleId)
      .join(", ");
  };

  const getUserNames = (userIds?: string[]) => {
    if (!userIds?.length) {
      return "N/A";
    }

    return userIds
      .map((userId) => userNameById.get(userId) || userId)
      .join(", ");
  };

  const getOrgUnitName = (orgUnitId?: string) => {
    if (!orgUnitId) {
      return "N/A";
    }

    return orgUnitNameById.get(orgUnitId) || orgUnitId;
  };

  const getApprovalTypeLabel = (step: any): string => {
    switch (step.approvers_type) {
      case "user":
        return `User: ${getUserNames(step.approver_ids)}`;
      case "role":
        return `Role: ${getRoleNames(step.approver_roles)}`;
      case "manager":
        return `Manager Level ${step.manager_level || "N/A"} (${getOrgUnitName(step.org_unit_id)})`;
      case "org_unit":
        return `Organization Unit: ${getOrgUnitName(step.org_unit_id)}`;
      default:
        return "Unknown";
    }
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
        Approval Flow:
      </h4>
      <div className="space-y-2">
        {chainDetails.steps?.map((step: any, index: number) => (
          <div key={step.id} className="flex items-center gap-3 text-sm">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-300 font-medium">
              {index + 1}
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-gray-50">
                {getApprovalTypeLabel(step)}
              </p>
              <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-4 mt-1">
                {step.parallel_approval && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                    Parallel
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  Required: {step.required_approvals || 1}
                </span>
              </div>
              {step.escalate_after_hours && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Escalates after {step.escalate_after_hours}h
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
